import { useEffect, useState, useCallback } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { rupiah, tanggalID, todayISO, toDateInput } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Wallet, HandCoins, CreditCard, CheckCircle2, Download, FileSpreadsheet } from "lucide-react";

const downloadFile = async (path, params, filename) => {
  const token = localStorage.getItem("khn_token");
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params).forEach(([k, v]) => v && url.searchParams.set(k, v));
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = filename; a.click();
};

const emptyForm = (tipe) => ({ tipe, pihak: "", kategori: "", keterangan: "", tanggal: todayISO(), jatuh_tempo: "", pokok: "" });

export default function HutangPiutang() {
  const [tipe, setTipe] = useState("utang");
  const [data, setData] = useState({ items: [], total_sisa: 0, total_pokok: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm("utang"));
  const [editing, setEditing] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState({ tanggal: todayISO(), jumlah: "", keterangan: "" });
  const [kategoriList, setKategoriList] = useState([]);
  const [filterKat, setFilterKat] = useState("");

  const isUtang = tipe === "utang";
  const load = useCallback(async () => {
    const r = await api.get("/hutang-piutang", { params: { tipe, kategori: filterKat || undefined } });
    setData(r.data);
  }, [tipe, filterKat]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/kategori", { params: { tipe: "hutang_piutang" } }).then((r) => setKategoriList(r.data)).catch(() => {}); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openNew = () => { setEditing(null); setForm(emptyForm(tipe)); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row, tanggal: toDateInput(row.tanggal) || todayISO(), jatuh_tempo: toDateInput(row.jatuh_tempo), pokok: row.pokok }); setOpen(true); };

  const save = async () => {
    if (!form.pihak?.trim()) { toast.error("Nama pihak wajib diisi"); return; }
    if (!form.tanggal) { toast.error("Tanggal wajib diisi"); return; }
    if (!(Number(form.pokok) > 0)) { toast.error("Jumlah wajib diisi (lebih dari 0)"); return; }
    const payload = { tipe, pihak: form.pihak.trim(), kategori: form.kategori, keterangan: form.keterangan, tanggal: form.tanggal, jatuh_tempo: form.jatuh_tempo || null, pokok: Number(form.pokok) };
    try {
      if (editing) await api.put(`/hutang-piutang/${editing.id}`, payload);
      else await api.post("/hutang-piutang", payload);
      toast.success("Tersimpan");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (row) => {
    if (!window.confirm(`Hapus ${tipe} "${row.pihak}"? Semua cicilan terkait ikut dibatalkan.`)) return;
    try { const r = await api.delete(`/hutang-piutang/${row.id}`); toast.success(`Dihapus${isUtang ? ` (${r.data.reversed_expenses} pengeluaran dibatalkan)` : ""}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openPay = (row) => { setPayFor(row); setPay({ tanggal: todayISO(), jumlah: String(row.sisa), keterangan: "" }); };
  const submitPay = async () => {
    if (!(Number(pay.jumlah) > 0)) { toast.error("Jumlah pembayaran wajib diisi"); return; }
    try {
      const r = await api.post(`/hutang-piutang/${payFor.id}/bayar`, { tanggal: pay.tanggal, jumlah: Number(pay.jumlah), keterangan: pay.keterangan });
      setPayFor(r.data); await load();
      toast.success("Pembayaran dicatat");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delPay = async (pid) => {
    if (!window.confirm("Hapus pembayaran ini?")) return;
    try { const r = await api.delete(`/hutang-piutang/${payFor.id}/bayar/${pid}`); setPayFor(r.data); await load(); toast.success("Pembayaran dihapus"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (<>
    <PageHeader title="Utang & Piutang" subtitle="Kelola utang & piutang dengan cicilan dan saldo berjalan."
      actions={<div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={() => downloadFile("/hutang-piutang/export", { fmt: "pdf", tipe, kategori: filterKat }, `${tipe}-${todayISO()}.pdf`)} className="rounded-sm" data-testid="hp-dl-pdf"><Download size={14} className="mr-1" /> PDF</Button>
        <Button variant="outline" onClick={() => downloadFile("/hutang-piutang/export", { fmt: "xlsx", tipe, kategori: filterKat }, `${tipe}-${todayISO()}.xlsx`)} className="rounded-sm" data-testid="hp-dl-xlsx"><FileSpreadsheet size={14} className="mr-1" /> Excel</Button>
        <Button onClick={openNew} className="rounded-sm" data-testid="hp-add-btn"><Plus size={16} className="mr-1" /> Tambah {isUtang ? "Utang" : "Piutang"}</Button>
      </div>} />

    <div className="p-4 md:p-8 space-y-6">
      {/* Tabs + filter kategori */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setTipe("utang")} className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors ${isUtang ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="hp-tab-utang"><CreditCard size={16} /> Utang</button>
        <button onClick={() => setTipe("piutang")} className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors ${!isUtang ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="hp-tab-piutang"><HandCoins size={16} /> Piutang</button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">Filter kategori:</span>
          <Select value={filterKat || "__all__"} onValueChange={(v) => setFilterKat(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-9 w-48" data-testid="hp-filter-kategori"><SelectValue placeholder="Semua Kategori" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Semua Kategori</SelectItem>
              {kategoriList.map((k) => <SelectItem key={k.id} value={k.nama}>{k.nama}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className={`rounded-sm p-4 ${isUtang ? "bg-rose-50" : "bg-emerald-50"}`} data-testid="hp-summary">
        <div className="text-[11px] uppercase tracking-wide opacity-70">Total Sisa {isUtang ? "Utang (Kewajiban)" : "Piutang (Aset)"}</div>
        <div className={`text-2xl font-semibold font-mono ${isUtang ? "text-rose-700" : "text-emerald-700"}`}>{rupiah(data.total_sisa)}</div>
        <div className="text-xs text-slate-500 mt-1">Total pokok {rupiah(data.total_pokok)} · Lunas {data.lunas || 0} · Belum lunas {data.belum_lunas || 0}</div>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="bg-slate-50 border-y text-slate-500 text-xs uppercase tracking-wide">
            <th className="p-3 text-left">Tanggal</th><th className="p-3 text-left">Pihak</th><th className="p-3 text-left">Kategori</th>
            <th className="p-3 text-left">Jatuh Tempo</th><th className="p-3 text-right">Pokok</th><th className="p-3 text-right">Dibayar</th>
            <th className="p-3 text-right">Sisa</th><th className="p-3 text-center">Status</th><th className="p-3 text-right">Aksi</th>
          </tr></thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-400">Belum ada data {isUtang ? "utang" : "piutang"}.</td></tr>}
            {data.items.map((r) => (
              <tr key={r.id} className="border-b" data-testid="hp-row">
                <td className="p-3 whitespace-nowrap">{tanggalID(r.tanggal)}</td>
                <td className="p-3 font-medium">{r.pihak}{r.keterangan ? <div className="text-xs text-slate-400 font-normal">{r.keterangan}</div> : null}</td>
                <td className="p-3 capitalize">{r.kategori || "-"}</td>
                <td className="p-3 whitespace-nowrap">{r.jatuh_tempo ? tanggalID(r.jatuh_tempo) : "-"}</td>
                <td className="p-3 text-right font-mono whitespace-nowrap">{rupiah(r.pokok)}</td>
                <td className="p-3 text-right font-mono whitespace-nowrap text-slate-500">{rupiah(r.dibayar)}</td>
                <td className="p-3 text-right font-mono whitespace-nowrap font-semibold">{rupiah(r.sisa)}</td>
                <td className="p-3 text-center">{r.status === "lunas"
                  ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded"><CheckCircle2 size={11} /> Lunas</span>
                  : <span className="text-[11px] text-amber-700 bg-amber-100 px-2 py-0.5 rounded">Belum lunas</span>}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    {r.status !== "lunas" && <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => openPay(r)} data-testid="hp-bayar-btn"><Wallet size={13} className="mr-1" /> Bayar</Button>}
                    <button onClick={() => openEdit(r)} className="text-slate-500 hover:text-slate-800 p-1 text-xs underline" data-testid="hp-edit-btn">Edit</button>
                    <button onClick={() => del(r)} className="text-rose-500 hover:text-rose-700 p-1" data-testid="hp-del-btn"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isUtang && <p className="text-xs text-slate-400">Setiap pembayaran utang otomatis dicatat sebagai Pengeluaran (kas berkurang, masuk laporan & perhitungan dividen).</p>}
    </div>

    {/* Add/Edit dialog */}
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="hp-form-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} {isUtang ? "Utang" : "Piutang"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>{isUtang ? "Pihak (Supplier/Bank)" : "Pihak (Peminjam)"} <span className="text-rose-500">*</span></Label><Input value={form.pihak} onChange={upd("pihak")} data-testid="hp-f-pihak" /></div>
          <div className="space-y-1.5"><Label>Kategori</Label>
            <Select value={form.kategori || undefined} onValueChange={(v) => setForm({ ...form, kategori: v })}>
              <SelectTrigger data-testid="hp-f-kategori"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
              <SelectContent>{kategoriList.map((k) => <SelectItem key={k.id} value={k.nama}>{k.nama}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Tanggal <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal} onChange={upd("tanggal")} data-testid="hp-f-tanggal" /></div>
          <div className="space-y-1.5"><Label>Jatuh Tempo</Label><Input type="date" value={form.jatuh_tempo} onChange={upd("jatuh_tempo")} data-testid="hp-f-jatuhtempo" /></div>
          <div className="space-y-1.5"><Label>Jumlah / Pokok (Rp) <span className="text-rose-500">*</span></Label><CurrencyInput value={form.pokok} onValueChange={(v) => setForm({ ...form, pokok: v })} data-testid="hp-f-pokok" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Keterangan</Label><Input value={form.keterangan} onChange={upd("keterangan")} data-testid="hp-f-keterangan" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button><Button onClick={save} className="rounded-sm" data-testid="hp-save-btn">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Payment dialog */}
    <Dialog open={!!payFor} onOpenChange={(v) => !v && setPayFor(null)}>
      <DialogContent data-testid="hp-pay-dialog">
        <DialogHeader><DialogTitle>Pembayaran — {payFor?.pihak}</DialogTitle></DialogHeader>
        {payFor && <>
          <div className="flex items-center justify-between text-sm bg-slate-50 rounded-sm p-3">
            <span>Pokok {rupiah(payFor.pokok)} · Dibayar {rupiah(payFor.dibayar)}</span>
            <span className="font-semibold">Sisa: {rupiah(payFor.sisa)}</span>
          </div>
          {payFor.status !== "lunas" && <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
            <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={pay.tanggal} onChange={(e) => setPay({ ...pay, tanggal: e.target.value })} data-testid="hp-pay-tanggal" /></div>
            <div className="space-y-1.5"><Label>Jumlah (Rp)</Label><CurrencyInput value={pay.jumlah} onValueChange={(v) => setPay({ ...pay, jumlah: v })} data-testid="hp-pay-jumlah" /></div>
            <Button onClick={submitPay} className="rounded-sm" data-enter-submit data-testid="hp-pay-submit">Catat</Button>
          </div>}
          <div className="mt-2">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Riwayat Pembayaran</div>
            {(payFor.pembayaran || []).length === 0 ? <p className="text-sm text-slate-400">Belum ada pembayaran.</p> :
              <div className="space-y-1 max-h-52 overflow-y-auto">
                {payFor.pembayaran.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm border-b py-1.5" data-testid="hp-pay-row">
                    <span>{tanggalID(p.tanggal)}</span>
                    <span className="font-mono">{rupiah(p.jumlah)}</span>
                    <button onClick={() => delPay(p.id)} className="text-rose-500 hover:text-rose-700"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>}
          </div>
        </>}
        <DialogFooter><Button variant="outline" onClick={() => setPayFor(null)} className="rounded-sm">Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}
